// ─────────────────────────────────────────────────────────────────────────
// ThreadContainer — chat window for the AgentForge feature.
//
// Self-driven: subscribes to the agentforge Zustand store for the active
// session + draft state, dispatches store actions for the header buttons
// (minimize / expand to fullscreen / close), and routes composer input
// through `useChatSubmit`.
//
// Renders in two modes via the `layout` prop:
//
//   - `'overlay'` (default) — fixed bottom-right floating panel. Mounted
//     globally by AgentForge; gated by `mode === 'floating'` + an active
//     tab/draft.
//
//   - `'inline'` — fills its parent box. Used as the page body on
//     `/agent/c/:sessionId`. No visibility gating: the route is the gate.
// ─────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Flex } from '@chakra-ui/react';

import { Header } from './components/Header';
import { ThreadBody } from './components/ThreadBody';
import { Footer } from './components/Footer';
import { FloatingActionsProvider } from '../components/floatingActions';

import { useThreadScroll } from './hooks/useThreadScroll';

import { useMessagesQuery } from '../api/messages';
import { latestMessageTime } from '../helpers';
import { AGENT_FORGE_LANDING_PATH, isAgentForgeThreadPath } from '../constants';
import {
  BOTTOM_OFFSET_PX,
  COLLAPSED_WIDTH_PX,
  DRAFT_SESSION_ID,
  DRAFT_TITLE,
  EMPTY_MESSAGES,
  EXPANDED_WIDTH_PX,
  HEIGHT_PX,
  RIGHT_OFFSET_PX,
  WIDTH_TRANSITION,
} from './constants';
import { getLastUserMessageId } from './utils/messageUtils';

import { useBarRouteVisibility, useChatSubmit } from '../hooks';
import { useAgentForgeStore } from '../stores';
import type { TranscriptMessage } from '../types';

import type { Message, Thread, WindowState } from './types/thread';
import type { ThreadContainerProps } from './ThreadContainer.types';

// ─── Store → ThreadContainer message adapter ─────────────────────────────

// Plain-text fallback used when an assistant message has zero blocks (older
// records or empty server payloads). AssistantBubble renders the registry-
// driven blocks when `blocks` is non-empty, otherwise falls back to content.
const firstTextContent = (
  outputs: { type: string; data: unknown }[]
): string => {
  const first = outputs.find((o) => o.type === 'text');
  if (!first) return '';
  return (first.data as { content?: string }).content ?? '';
};

// HTTP polling has no token-streaming: an assistant message is either
// absent (still being generated server-side) or complete. ThreadBody renders
// the trailing "Thinking…" SpinnerMessage separately when the transcript
// ends with a user turn, so individual assistant rows always set
// `in_progress: false` — otherwise every historic assistant message turns
// back into a spinner the moment a new prompt is sent.
const toThreadMessage = (
  message: TranscriptMessage,
  sessionId: string
): Message => {
  if (message.role === 'user') {
    return {
      id: message.id,
      session_id: sessionId,
      role: 'user',
      content: message.content,
      created_at: message.createdAt,
      in_progress: false,
      new_message: false,
      metadata: message.attachments?.length
        ? { attachments: message.attachments }
        : undefined,
    };
  }
  return {
    id: message.id,
    session_id: sessionId,
    role: 'assistant',
    content: firstTextContent(message.outputs),
    blocks: message.outputs,
    actionTrail: message.actionTrail,
    created_at: message.createdAt,
    in_progress: false,
    new_message: false,
  };
};

const ThreadContainer = ({
  layout = 'overlay',
  initialWindowState = 'collapsed',
}: ThreadContainerProps = {}) => {
  const visible = useBarRouteVisibility();
  const mode = useAgentForgeStore((s) => s.mode);
  const activeSessionId = useAgentForgeStore((s) => s.activeSessionId);
  const draftOpen = useAgentForgeStore((s) => s.draftOpen);
  const closeTab = useAgentForgeStore((s) => s.closeTab);
  const closeDraft = useAgentForgeStore((s) => s.closeDraft);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const tab = useAgentForgeStore((s) =>
    s.openTabs.find((t) => t.sessionId === activeSessionId)
  );
  const messages =
    useAgentForgeStore((s) =>
      activeSessionId ? s.messagesBySession[activeSessionId] : undefined
    ) ?? EMPTY_MESSAGES;
  const streamingDraft = useAgentForgeStore((s) => s.streamingDraft);
  const { send } = useChatSubmit();

  // Subscribe to the same messages query `useAgentForgeMessages` runs at
  // the AgentForge umbrella — TanStack dedupes by queryKey so this is a
  // read-only handle on the existing cache entry, not a second network
  // call. `isLoading` is true *only* on the very first fetch for a given
  // sessionId (no cached data); background refetches keep it false, which
  // is exactly the "first load only" semantics we want.
  const messagesQuery = useMessagesQuery({
    sessionId: activeSessionId,
    // 'pending-' ids are local instant-feedback placeholders (empty-state
    // submit) — the server doesn't know them, so a GET would just 404.
    enabled: !!activeSessionId && !activeSessionId.startsWith('pending-'),
  });
  // Suppress the shimmer the moment we have any messages locally — a
  // freshly-created session from the empty-state submit has an optimistic
  // user bubble in the store before the GET completes, and we'd rather
  // show that bubble than flash a placeholder over it.
  const isInitialLoading = messagesQuery.isLoading && messages.length === 0;

  const isInline = layout === 'inline';
  const { pathname } = useLocation();
  const history = useHistory();

  // The /agent route already mounts an inline ThreadContainer that owns
  // the chat surface. Suppress the global overlay there so the user
  // doesn't see two copies of the same conversation.
  const isOnAgentRoute = pathname.startsWith('/agent');

  // Visibility gates apply only to the overlay layout. On the dedicated
  // `/agent/c/:sessionId` route the page itself controls when we render.
  const overlayHidden =
    !isInline &&
    (!visible || mode !== 'floating' || isOnAgentRoute || (!tab && !draftOpen));

  // Adapt the store snapshot to ThreadContainer's Thread shape. Memoized on
  // the identity of upstream slices so unrelated renders don't rebuild the
  // messages array.
  const sessionId = tab?.sessionId ?? activeSessionId ?? DRAFT_SESSION_ID;

  // Only use the draft if it belongs to this session. A stale draft from a
  // previous session should not bleed into the current view.
  const draft = streamingDraft?.sessionId === sessionId ? streamingDraft : null;
  const draftHasContent = !!draft && draft.content.length > 0;
  const draftHasSubSteps = !!draft && draft.subSteps.length > 0;

  // We're awaiting a reply only while the tab is still streaming or a WS
  // draft is active. After `stream_done` flips the tab to `done`, this
  // turns off — even if the refetch hasn't landed yet — so we don't flash
  // a "Thinking…" card between the cleared accumulator and the canonical
  // message arriving. Matches legacy: legacy never infers a spinner from
  // "last message is the user".
  const isAwaitingReply = tab?.status === 'streaming' || !!draft;

  const thread: Thread = useMemo(() => {
    const adapted = messages.map((m) => toThreadMessage(m, sessionId));
    // Render the still-arriving text + any tool-result blocks (candidate
    // lists, action results) as a temporary assistant bubble. Replaced by
    // the persisted message once `stream_done` fires.
    const hasDraftOutputs = !!draft && draft.outputs.length > 0;
    if (draft && (draftHasContent || hasDraftOutputs)) {
      const draftBlocks = [
        ...(draftHasContent
          ? [{ type: 'text' as const, data: { content: draft.content } }]
          : []),
        ...draft.outputs,
      ];
      adapted.push({
        id: '__streaming-draft__',
        session_id: sessionId,
        role: 'assistant',
        content: draft.content,
        blocks: draftBlocks,
        created_at: new Date().toISOString(),
        in_progress: false,
        new_message: false,
      });
    }
    return {
      id: sessionId,
      title: tab?.title ?? DRAFT_TITLE,
      messages: adapted,
    };
  }, [sessionId, tab?.title, messages, draftHasContent, draft]);

  // Local width state is fixed at the collapsed 430px in overlay mode — the
  // Expand button promotes to fullscreen via the store rather than widening
  // the panel in place. Inline mode ignores width entirely (fills parent).
  const state: WindowState = initialWindowState;
  const width = state === 'expanded' ? EXPANDED_WIDTH_PX : COLLAPSED_WIDTH_PX;

  // Pin the latest user message to the top on send. Trigger uses a *user*
  // message id so assistant streaming chunks don't keep retriggering scroll.
  const { scrollRef } = useThreadScroll({
    trigger: getLastUserMessageId(thread.messages),
  });

  if (overlayHidden) return null;

  const handleClose = () => {
    if (tab) closeTab(tab.sessionId);
    else closeDraft();
    // Reset so the overlay doesn't reappear on the next navigation. Inline
    // close on /agent/c routes navigates back to the landing instead.
    setMode('docked');
    if (isOnAgentRoute && isAgentForgeThreadPath(pathname)) {
      history.push(AGENT_FORGE_LANDING_PATH);
    }
  };

  // Outer wrapper styling diverges between layouts; the inner children are
  // the same in both.
  const overlayStyles = {
    pos: 'fixed' as const,
    bottom: `${BOTTOM_OFFSET_PX}px`,
    right: `${RIGHT_OFFSET_PX}px`,
    zIndex: 'overlay' as const,
    w: `${width}px`,
    h: `${HEIGHT_PX}px`,
    borderRadius: '6px',
    border: '1px solid',
    borderColor: 'secondary-100',
    boxShadow: '0px 18px 40px rgba(38, 34, 68, 0.12)',
    sx: { transition: WIDTH_TRANSITION },
  } as const;

  const inlineStyles = {
    w: '100%',
    h: '100%',
  } as const;

  return (
    <Flex
      key={sessionId}
      data-test-id="thread-container"
      data-layout={layout}
      role="dialog"
      aria-label={`Chat: ${thread.title}`}
      direction="column"
      bg="base-white"
      overflow="hidden"
      {...(isInline ? inlineStyles : overlayStyles)}
    >
      <FloatingActionsProvider>
        <Header
          title={thread.title}
          createdAt={latestMessageTime(messages, tab?.createdAt)}
          onClose={handleClose}
        />
        <ThreadBody
          messages={thread.messages}
          scrollRef={scrollRef}
          isAwaitingReply={isAwaitingReply}
          isInitialLoading={isInitialLoading}
          streamingSubSteps={draftHasSubSteps ? draft?.subSteps : undefined}
          bodyContainerProps={
            isInline ? { maxW: EXPANDED_WIDTH_PX + 'px' } : undefined
          }
        />
        <Footer
          onSubmit={send}
          contentMaxWidth={isInline ? EXPANDED_WIDTH_PX + 'px' : '100%'}
          isAwaiting={isAwaitingReply}
        />
      </FloatingActionsProvider>
    </Flex>
  );
};

export default ThreadContainer;
export type { ThreadContainerProps };
